<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1" />
    </section>

    <section class="dp-right">
      <!-- 顶部固定头部栏（去除无用搜索，固定标题与操作按钮） -->
      <div class="config-fixed-header">
        <div class="config-fixed-header__left">
          <span class="config-fixed-header__eyebrow">校车管理 · 规则配置</span>
          <div class="config-fixed-header__title-row">
            <h2 class="config-fixed-header__title">
              {{ departmentId > 0 ? (departmentName || ('学校 #' + departmentId)) : '请在左侧选择学校' }}
            </h2>
            <el-tag v-if="departmentId > 0" size="mini" type="info" class="school-id-tag">
              学校ID: {{ departmentId }}
            </el-tag>
          </div>
        </div>
        <div class="config-fixed-header__actions" v-if="departmentId > 0">
          <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="loadConfig">
            刷新配置
          </el-button>
          <el-button type="primary" size="small" icon="el-icon-check" :loading="saving" @click="saveConfig">
            保存配置
          </el-button>
        </div>
      </div>

      <div class="config-panel">
        <!-- loading 状态 -->
        <div v-if="loading" class="loading-container" style="min-height: 300px;">
          <div class="loading-content">
            <i class="el-icon-loading" style="font-size: 32px; color: var(--btn-primary, #2563eb); margin-bottom: 10px;"></i>
            <div class="loading-text">正在加载校车配置...</div>
          </div>
        </div>

        <!-- 选中学校后显示 -->
        <div v-else-if="departmentId > 0" class="config-content">
          <div class="config-cards-container">
            <!-- 核心调度与自动离校配置卡片 -->
            <el-card class="config-card" shadow="never">
              <div slot="header" class="card-header">
                <span class="title"><i class="el-icon-time" style="color: #409EFF; margin-right: 6px;"></i>自动考勤与定时任务设置</span>
                <el-tag :type="form.enabled ? 'success' : 'info'" size="small">
                  {{ form.enabled ? '自动离校考勤已开启' : '自动离校考勤已停用' }}
                </el-tag>
              </div>

              <el-form :model="form" label-width="140px" ref="formRef" size="small">
                <div class="sub-section">
                  <div class="inner-card general-bg">
                    <div class="inner-title"><i class="el-icon-switch-button"></i> 基础启停开关</div>
                    <el-form-item label="功能总开关">
                      <div class="switch-row">
                        <el-switch
                          v-model="form.enabled"
                          active-color="#13ce66"
                          inactive-color="#ff4949"
                          active-text="开启"
                          inactive-text="关闭">
                        </el-switch>
                        <span class="mc-item-hint">开启后系统将按指定频率自动检查班主任排班，并在坐车时间到达时自动生成离校流水并给家长推送「离开校园」微信通知</span>
                      </div>
                    </el-form-item>
                  </div>
                </div>

                <div class="sub-section" style="margin-top: 16px;">
                  <div class="inner-card schedule-bg">
                    <div class="inner-title"><i class="el-icon-alarm-clock"></i> 调度频率与时间规则</div>

                    <el-form-item label="任务执行频率">
                      <el-radio-group v-model="form.intervalMinutes">
                        <el-radio :label="1">每 1 分钟</el-radio>
                        <el-radio :label="3">每 3 分钟</el-radio>
                        <el-radio :label="5">每 5 分钟（推荐）</el-radio>
                        <el-radio :label="10">每 10 分钟</el-radio>
                        <el-radio :label="15">每 15 分钟</el-radio>
                      </el-radio-group>
                      <p class="mc-item-hint">由系统 Quartz 后台任务独立按此频率调度当前学校的到点检测，不占用全平台全局执行资源</p>
                    </el-form-item>

                    <el-form-item label="默认坐车时间">
                      <div class="field-with-hint">
                        <el-time-picker
                          v-model="form.defaultRideTime"
                          format="HH:mm"
                          value-format="HH:mm"
                          placeholder="如 17:30"
                          :clearable="false"
                          style="width: 160px;">
                        </el-time-picker>
                        <span class="mc-item-hint">班主任排班时默认带出的坐车时间，支持班主任在排班界面针对单人单独修改</span>
                      </div>
                    </el-form-item>

                    <el-form-item label="补推容差时间">
                      <div class="field-with-hint">
                        <el-input-number v-model="form.toleranceMinutes" :min="1" :max="120" :step="5" style="width: 160px;"></el-input-number>
                        <span class="mc-item-hint">分钟。若服务器偶发重启或短暂中断，只补处理最近容差窗口内的排班，防止严重逾期补推骚扰家长</span>
                      </div>
                    </el-form-item>

                    <el-form-item label="周末自动跳过">
                      <el-switch v-model="form.skipWeekends" active-text="周六周日不执行" inactive-text="周末正常执行"></el-switch>
                      <p class="mc-item-hint">启用后，周六与周日将自动跳过自动离校推送</p>
                    </el-form-item>
                  </div>
                </div>

                <!-- 定时任务信息 -->
                <div class="sub-section" style="margin-top: 16px;">
                  <div class="inner-card task-bg">
                    <div class="inner-title"><i class="el-icon-connection"></i> Quartz 定时任务绑定（TasksQz）</div>
                    <div class="moral-task-row">
                      <span class="moral-item-label">调度任务 ID：</span>
                      <div class="moral-task-text">
                        <span v-if="form.taskId" class="task-pill">
                          校车自动考勤：<code>{{ form.taskId }}</code>
                          <el-tag size="mini" type="success" style="margin-left: 6px;">运行中</el-tag>
                        </span>
                        <span v-else class="text-muted">
                          未生成任务（请开启功能开关并点击右上方「保存配置」自动挂载 Quartz 任务）
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </el-form>
            </el-card>

            <!-- 业务运行机制与兼容说明卡片 -->
            <el-card class="config-card" shadow="never" style="margin-top: 16px;">
              <div slot="header" class="card-header">
                <span class="title"><i class="el-icon-info" style="color: #67C23A; margin-right: 6px;"></i>业务运作机制与兼容说明</span>
              </div>
              <div class="info-content">
                <el-alert
                  title="多源兼容机制"
                  type="success"
                  :closable="false"
                  description="若学校已有真实校车设备或移动车载 APP，现有车载终端考勤流程完全保持不变。班主任自动排班生成的流水固定标注为「校车自动考勤终端」与「离开校园」，二者在统计后台同时呈现并支持按来源筛选统计。"
                  show-icon>
                </el-alert>
                <div class="info-list">
                  <div class="info-item"><i class="el-icon-circle-check"></i> 班主任排班入口：可在「校车管理 - 班主任排班」中提前最长一周规划每天坐车学生。</div>
                  <div class="info-item"><i class="el-icon-circle-check"></i> 幂等防重保障：任务到点触发时具备乐观锁抢占机制与流水唯一索引，保证同一时间点通知不被并发重复触发。</div>
                  <div class="info-item"><i class="el-icon-circle-check"></i> 统计汇总报表：已在「校车管理 - 乘车统计汇总」中提供学生月度、每日及班级维度的离校次数明细与 Excel 导出。</div>
                </div>
              </div>
            </el-card>
          </div>
        </div>

        <!-- 未选择学校提示 -->
        <div v-else class="empty-placeholder">
          <i class="el-icon-school" style="font-size: 48px; color: #c0c4cc; margin-bottom: 16px;"></i>
          <p>请在左侧学校列表中选择要配置的学校</p>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import DepartmentPane from '@/components/DepartmentPane';
import { getSchoolBusConfig, saveSchoolBusConfig } from '@/api/modules/schoolBusSchedule';

export default {
  name: 'SchoolBusConfig',
  components: {
    DepartmentPane
  },
  data() {
    return {
      departmentId: 0,
      departmentName: '',
      loading: false,
      saving: false,
      form: {
        schoolDepartmentId: 0,
        enabled: false,
        intervalMinutes: 5,
        defaultRideTime: '17:30',
        toleranceMinutes: 15,
        skipWeekends: true,
        taskId: ''
      }
    };
  },
  methods: {
    treeNodeClick(data) {
      const deptId = data ? (data.value !== undefined ? data.value : data.id) : 0;
      if (!deptId) return;
      this.departmentId = deptId;
      this.departmentName = data.label || data.name || '';
      this.loadConfig();
    },
    async loadConfig() {
      if (!this.departmentId) return;
      this.loading = true;
      try {
        const res = await getSchoolBusConfig({ schoolDepartmentId: this.departmentId });
        if (res.data && res.data.success && res.data.response) {
          const cfg = res.data.response;
          const enabledVal = cfg.Enabled !== undefined ? cfg.Enabled : cfg.enabled;
          const intervalVal = cfg.IntervalMinutes !== undefined ? cfg.IntervalMinutes : cfg.intervalMinutes;
          const defaultRideTimeVal = cfg.DefaultRideTime !== undefined ? cfg.DefaultRideTime : cfg.defaultRideTime;
          const toleranceMinutesVal = cfg.ToleranceMinutes !== undefined ? cfg.ToleranceMinutes : cfg.toleranceMinutes;
          const skipWeekendsVal = cfg.SkipWeekends !== undefined ? cfg.SkipWeekends : cfg.skipWeekends;
          const taskIdVal = cfg.TaskId !== undefined ? cfg.TaskId : cfg.taskId;

          this.form = {
            schoolDepartmentId: this.departmentId,
            enabled: !!enabledVal,
            intervalMinutes: intervalVal || 5,
            defaultRideTime: defaultRideTimeVal || '17:30',
            toleranceMinutes: toleranceMinutesVal || 15,
            skipWeekends: skipWeekendsVal !== false,
            taskId: taskIdVal || ''
          };
        } else {
          const errMsg = (res.data && res.data.msg) || '获取校车配置失败';
          this.$message.error(errMsg);
        }
      } catch (err) {
        this.$message.error('加载校车配置异常：' + (err.message || '网络错误'));
      } finally {
        this.loading = false;
      }
    },
    async saveConfig() {
      if (!this.departmentId) {
        this.$message.warning('请先在左侧选择学校');
        return;
      }
      this.saving = true;
      try {
        const payload = {
          schoolDepartmentId: this.departmentId,
          enabled: this.form.enabled,
          intervalMinutes: this.form.intervalMinutes,
          defaultRideTime: this.form.defaultRideTime,
          toleranceMinutes: this.form.toleranceMinutes,
          skipWeekends: this.form.skipWeekends
        };
        const res = await saveSchoolBusConfig(payload);
        if (res.data && res.data.success) {
          this.$message.success('校车配置保存成功');
          const resp = res.data.response;
          const taskId = resp && (resp.TaskId !== undefined ? resp.TaskId : resp.taskId);
          if (taskId !== undefined) {
            this.form.taskId = taskId || '';
          }
        } else {
          const errMsg = (res.data && res.data.msg) || '保存配置失败';
          this.$message.error(errMsg);
        }
      } catch (err) {
        this.$message.error('保存配置异常：' + (err.message || '网络错误'));
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.dp-layout {
  display: flex;
  height: 100%;
  width: 100%;
  background: #f5f7fa;
}

.dp-left {
  width: 260px;
  border-right: 1px solid #e4e7ed;
  background: #fff;
  height: 100%;
  overflow-y: auto;
}

.dp-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.config-panel {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.config-fixed-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 24px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
  z-index: 10;
}

.config-fixed-header__left {
  display: flex;
  flex-direction: column;
}

.config-fixed-header__eyebrow {
  font-size: 12px;
  color: #909399;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.config-fixed-header__title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.config-fixed-header__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  line-height: 1.3;
}

.school-id-tag {
  font-weight: normal;
  border-radius: 4px;
}

.config-fixed-header__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.config-cards-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.config-card {
  border-radius: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header .title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.inner-card {
  border-radius: 6px;
  padding: 16px 20px;
  border: 1px solid #ebeef5;
}

.general-bg {
  background: #fbfdff;
  border-color: #e1f0fe;
}

.schedule-bg {
  background: #fdfdfd;
  border-color: #ebeef5;
}

.task-bg {
  background: #fdfbf7;
  border-color: #faecd8;
}

.inner-title {
  font-size: 14px;
  font-weight: 600;
  color: #409EFF;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.mc-item-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.switch-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.moral-task-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #fff;
  border: 1px dashed #dcdfe6;
}

.moral-item-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.task-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  background: #f0f7ff;
  border: 1px solid #d0e5ff;
  font-size: 13px;
  color: #2b6cb0;
}

.task-pill code {
  font-family: Consolas, Monaco, monospace;
  font-weight: bold;
  margin-left: 4px;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
}

.info-item {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}

.info-item i {
  color: #67c23a;
  margin-right: 6px;
}

.field-with-hint {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #909399;
}

@media screen and (max-width: 768px) {
  /* 顶部固定栏转为适合手机视口与单手操作的纵向卡片结构 */
  .config-fixed-header {
    flex-direction: column;
    align-items: stretch;
    padding: 12px 14px;
    gap: 12px;
  }
  .config-fixed-header__left {
    width: 100%;
  }
  .config-fixed-header__title-row {
    flex-wrap: wrap;
    gap: 8px;
  }
  .config-fixed-header__title {
    font-size: 16px;
    word-break: break-all;
  }
  .config-fixed-header__actions {
    display: flex;
    width: 100%;
    gap: 10px;
  }
  .config-fixed-header__actions .el-button {
    flex: 1;
    margin-left: 0 !important;
    justify-content: center;
  }

  /* 面板与卡片内边距紧凑化，提升小屏利用率 */
  .config-panel {
    padding: 8px 0;
  }
  /deep/ .el-card__body {
    padding: 12px 10px !important;
  }
  /deep/ .el-card__header {
    padding: 12px 10px !important;
  }
  .card-header {
    flex-wrap: wrap;
    gap: 8px;
  }
  .card-header .title {
    font-size: 14px;
  }
  .inner-card {
    padding: 12px 10px;
  }

  /* 表单项由 PC 端的固定 label-width=140px 转为适合手机的块级上下堆叠 */
  /deep/ .el-form-item {
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
  }
  /deep/ .el-form-item__label {
    width: 100% !important;
    text-align: left !important;
    float: none !important;
    line-height: 1.5;
    margin-bottom: 6px;
    padding: 0 !important;
    font-weight: 600;
    color: #303133;
  }
  /deep/ .el-form-item__content {
    margin-left: 0 !important;
    width: 100% !important;
  }

  /* 功能总开关在手机端纵向排列，说明文案全宽展开，不再挤成竖条字 */
  .switch-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .switch-row .mc-item-hint {
    line-height: 1.5;
    margin-top: 4px;
  }

  /* 调度频率单选组自适应换行流式布局 */
  /deep/ .el-radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 14px;
  }
  /deep/ .el-radio {
    margin-right: 0 !important;
  }

  /* Quartz任务信息框在手机端自适应折行 */
  .moral-task-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  .moral-task-text {
    width: 100%;
  }
  .task-pill {
    max-width: 100%;
    flex-wrap: wrap;
    border-radius: 8px;
    padding: 6px 10px;
    line-height: 1.5;
    word-break: break-all;
  }
}
</style>
